// Arboricultural inspection module — camelCase frontend types
// Matches backend schemas in backend/app/schemas/arboricultural_inspection.py

export const INSPECTION_TYPES = [
  "INVENTORY_BASELINE",
  "ARBORICULTURAL_TECHNICAL",
  "MONITORING",
  "POST_MANAGEMENT",
  "EMERGENCY",
  "SUPERVISION",
  "OTHER",
] as const;

export type InspectionType = (typeof INSPECTION_TYPES)[number];

export const WORKFLOW_STATUSES = [
  "DRAFT",
  "IN_PROGRESS",
  "SUBMITTED",
  "UNDER_REVIEW",
  "RETURNED",
  "VALIDATED",
  "CONCLUDED",
  "CANCELLED",
] as const;

export type WorkflowStatus = (typeof WORKFLOW_STATUSES)[number];

export const HEALTH_FINDING_TYPES = [
  "CAVITY",
  "WOOD_DECAY",
  "NECROSIS",
  "CRACK",
  "BREAKAGE",
  "GIRDLING",
  "CANKER",
  "WOUND",
  "TERMITES",
  "BORERS",
  "WOOD_DECAY_FUNGI",
  "MISTLETOE",
  "OTHER",
] as const;

export const MECHANICAL_FINDING_TYPES = [
  "CODOMINANCE",
  "INCLUDED_BARK",
  "BROKEN_BRANCH",
  "DEAD_BRANCH",
  "STRUCTURAL_CAVITY",
  "STRUCTURAL_CRACK",
  "CROWN_IMBALANCE",
  "ROOT_PLATE_MOVEMENT",
  "ANCHORAGE_LOSS",
  "OTHER",
] as const;

export const FINDING_LOCATIONS = [
  "ROOT_COLLAR",
  "TRUNK",
  "BIFURCATION",
  "BRANCH",
  "CROWN",
  "ROOT",
  "OTHER",
] as const;

export const SEVERITY_LEVELS = ["LOW", "MODERATE", "HIGH", "NOT_ASSESSED"] as const;

export const RISK_TARGET_TYPES = [
  "PEDESTRIAN",
  "VEHICLE",
  "RESIDENCE",
  "COMMERCIAL_BUILDING",
  "SCHOOL",
  "PUBLIC_FACILITY",
  "ELECTRICAL_NETWORK",
  "ROAD",
  "URBAN_FURNITURE",
  "OTHER",
] as const;

export const OCCUPANCY_LEVELS = ["LOW", "MEDIUM", "HIGH", "VERY_HIGH", "UNKNOWN"] as const;

export const EVIDENCE_TYPES = [
  "GENERAL_TREE",
  "CROWN",
  "TRUNK",
  "ROOT_COLLAR",
  "ROOTS",
  "DEFECT",
  "PEST_DISEASE",
  "LEAVES_FLOWERS_FRUITS",
  "TAG_PLATE",
  "INTERVENTION",
  "SIDEWALK",
  "POWER_GRID",
  "POLE_LIGHTING",
  "BUILDING_WALL",
  "ROAD_TRAFFIC",
  "URBAN_CONFLICT",
  "RISK_TARGET",
  "PANORAMIC",
  "OTHER",
] as const;

// --- Read sub-types (from API response) ---

export interface TreeMeasurement {
  id: number;
  dapCm: number | null;
  capCm: number | null;
  heightM: number | null;
  crownDiameterM: number | null;
  inclinationDeg: number | null;
  measurementSource: string | null;
  createdAt: string;
}

export interface StemMeasurement {
  id: number;
  uuid: string;
  sequence: number;
  capCm: number | null;
  dapCm: number | null;
  measurementMethod: string | null;
  heightM: number | null;
  notes: string | null;
  createdAt: string;
}

export interface HealthAssessment {
  id: number;
  generalStatus: string | null;
  /** F1-F4, derivada da condicao geral (catalogo MFI-AU). */
  phytosanitaryClass: string | null;
  /** R1-R4 (classe radicular). */
  rootCondition: string | null;
  rootNotes: string | null;
  pests: boolean | null;
  diseases: boolean | null;
  fungi: boolean | null;
  termites: boolean | null;
  cavities: boolean | null;
  rot: boolean | null;
  dryBranches: boolean | null;
  exposedRoots: boolean | null;
  abnormalInclination: boolean | null;
  inclinationAngleDeg: number | null;
  inclinationDirection: string | null;
  exposedRootsDetail: string | null;
  underminedRoots: boolean | null;
  damagedRoots: boolean | null;
  constructionDamage: boolean | null;
  soilCompaction: boolean | null;
  rootPlateMovement: boolean | null;
  notes: string | null;
  createdAt: string;
}

export interface HealthFinding {
  id: number;
  uuid: string;
  findingType: string;
  location: string | null;
  severity: string | null;
  notes: string | null;
  createdAt: string;
}

export interface MechanicalFinding {
  id: number;
  uuid: string;
  findingType: string;
  location: string | null;
  severity: string | null;
  notes: string | null;
  createdAt: string;
}

export interface RiskAssessment {
  id: number;
  riskClass: string | null;
  riskScore: number | null;
  priority: string | null;
  pedestrians: boolean | null;
  vehicles: boolean | null;
  buildings: boolean | null;
  powerGrid: boolean | null;
  riskCriteria: Record<string, unknown> | null;
  /** A0-A2 (exposicao de alvos). */
  targetClass: string | null;
  /** P0-P4 (prioridade operacional, exemplo provisorio). */
  priorityClass: string | null;
  priorityDueAt: string | null;
  priorityDeadlineLabel: string | null;
  prioritySuggested: string | null;
  prioritySuggestionReasons: string[];
  notes: string | null;
  createdAt: string;
}

export interface RiskTarget {
  id: number;
  uuid: string;
  targetType: string;
  occupancy: string | null;
  notes: string | null;
  createdAt: string;
}

export interface Interference {
  id: number;
  interferenceType: string | null;
  intensity: string | null;
  description: string | null;
  createdAt: string;
}

export interface Recommendation {
  id: number;
  recommendationType: string | null;
  priority: string | null;
  justification: string | null;
  status: string | null;
  createdAt: string;
}

export interface Evidence {
  id: number;
  uuid: string;
  evidenceType: string | null;
  filePath: string;
  thumbnailPath: string | null;
  originalFilename: string | null;
  mimeType: string | null;
  fileSizeBytes: number | null;
  description: string | null;
  latitude: number | null;
  longitude: number | null;
  capturedAt: string | null;
  uploadedAt: string;
  createdAt: string;
  /** Invalidada: o arquivo, o hash e a autoria continuam guardados. */
  invalidatedAt: string | null;
  invalidatedBy: number | null;
  invalidationReason: string | null;
}

export interface Compensation {
  id: number;
  uuid: string;
  compensationIndicated: boolean;
  proposedSeedlingQuantity: number | null;
  proposedSpecies: unknown[] | Record<string, unknown> | null;
  notes: string | null;
  createdAt: string;
}

// --- Complete inspection DTO ---

export interface ArborInspectionDetail {
  id: number;
  uuid: string;
  tenantId: number;
  projectId: number;
  treeId: number;
  inspectionType: string;
  inspectionDate: string;
  workflowStatus: WorkflowStatus;
  status: string;
  inspectorName: string | null;
  professionalCouncil: string | null;
  professionalRegistration: string | null;
  locationConfirmed: boolean;
  botanicalIdentificationStatus: string | null;
  botanicalNotes: string | null;
  diagnosis: string | null;
  technicalOpinion: string | null;
  requiresMonitoring: boolean;
  nextInspectionSuggestedAt: string | null;
  notes: string | null;
  version: number;
  submittedAt: string | null;
  submittedBy: number | null;
  submittedByName: string | null;
  validatedAt: string | null;
  validatedBy: number | null;
  validatedByName: string | null;
  returnedAt: string | null;
  returnedBy: number | null;
  returnedByName: string | null;
  returnReason: string | null;
  cancelledAt: string | null;
  cancelledBy: number | null;
  cancelledByName: string | null;
  cancelReason: string | null;
  createdAt: string;
  updatedAt: string;

  measurement: TreeMeasurement | null;
  stems: StemMeasurement[];
  healthAssessment: HealthAssessment | null;
  healthFindings: HealthFinding[];
  mechanicalFindings: MechanicalFinding[];
  riskAssessment: RiskAssessment | null;
  riskTargets: RiskTarget[];
  interferences: Interference[];
  recommendations: Recommendation[];
  evidence: Evidence[];
  compensation: Compensation | null;
}

// --- Summary (list item) ---

export interface ArborInspectionSummary {
  id: number;
  uuid: string;
  treeId: number;
  inspectionType: string;
  inspectionDate: string;
  workflowStatus: WorkflowStatus;
  inspectorName: string | null;
  notes: string | null;
  createdAt: string;
  dapCm: number | null;
  heightM: number | null;
  healthStatus: string | null;
  riskClass: string | null;
  riskPriority: string | null;
  evidenceCount: number;
  recommendationCount: number;
}

// --- Input types (for create/update payloads) ---

export interface ArborInspectionCreateInput {
  inspectionType: string;
  inspectionDate: string;
  notes?: string | null;
}

export interface StemMeasurementInput {
  sequence: number;
  capCm?: number | null;
  dapCm?: number | null;
  measurementMethod?: string | null;
  heightM?: number | null;
  notes?: string | null;
}

export interface HealthFindingInput {
  findingType: string;
  location?: string | null;
  severity?: string | null;
  notes?: string | null;
}

export interface MechanicalFindingInput {
  findingType: string;
  location?: string | null;
  severity?: string | null;
  notes?: string | null;
}

export interface RiskTargetInput {
  targetType: string;
  occupancy?: string | null;
  notes?: string | null;
}

export interface InterferenceInput {
  interferenceType?: string | null;
  intensity?: string | null;
  description?: string | null;
}

export interface RecommendationInput {
  recommendationType?: string | null;
  priority?: string | null;
  justification?: string | null;
}

export interface CompensationInput {
  compensationIndicated: boolean;
  proposedSeedlingQuantity?: number | null;
  proposedSpecies?: unknown[] | Record<string, unknown> | null;
  notes?: string | null;
}

export interface MeasurementInput {
  dapCm?: number | null;
  capCm?: number | null;
  heightM?: number | null;
  crownDiameterM?: number | null;
  inclinationDeg?: number | null;
  measurementSource?: string | null;
}

export interface HealthAssessmentInput {
  generalStatus?: string | null;
  rootCondition?: string | null;
  rootNotes?: string | null;
  pests?: boolean | null;
  diseases?: boolean | null;
  fungi?: boolean | null;
  termites?: boolean | null;
  cavities?: boolean | null;
  rot?: boolean | null;
  dryBranches?: boolean | null;
  exposedRoots?: boolean | null;
  abnormalInclination?: boolean | null;
  inclinationAngleDeg?: number | null;
  inclinationDirection?: string | null;
  exposedRootsDetail?: string | null;
  underminedRoots?: boolean | null;
  damagedRoots?: boolean | null;
  constructionDamage?: boolean | null;
  soilCompaction?: boolean | null;
  rootPlateMovement?: boolean | null;
  notes?: string | null;
}

export interface RiskAssessmentInput {
  riskClass?: string | null;
  riskScore?: number | null;
  priority?: string | null;
  pedestrians?: boolean | null;
  vehicles?: boolean | null;
  buildings?: boolean | null;
  powerGrid?: boolean | null;
  riskCriteria?: Record<string, unknown> | null;
  targetClass?: string | null;
  priorityClass?: string | null;
  notes?: string | null;
}

export interface ArborInspectionUpdateInput {
  inspectionDate?: string | null;
  notes?: string | null;
  inspectorName?: string | null;
  professionalCouncil?: string | null;
  professionalRegistration?: string | null;
  locationConfirmed?: boolean | null;
  botanicalIdentificationStatus?: string | null;
  botanicalNotes?: string | null;
  diagnosis?: string | null;
  technicalOpinion?: string | null;
  requiresMonitoring?: boolean | null;
  nextInspectionSuggestedAt?: string | null;

  measurement?: MeasurementInput | null;
  stems?: StemMeasurementInput[] | null;
  healthAssessment?: HealthAssessmentInput | null;
  healthFindings?: HealthFindingInput[] | null;
  mechanicalFindings?: MechanicalFindingInput[] | null;
  riskAssessment?: RiskAssessmentInput | null;
  riskTargets?: RiskTargetInput[] | null;
  interferences?: InterferenceInput[] | null;
  recommendations?: RecommendationInput[] | null;
  compensation?: CompensationInput | null;
}
